import clsx from 'clsx';
import { ArrowDownRightIcon, ArrowUpRightIcon, CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Panel } from '../../../components/panels/Panel';
import { formatDemoNumber, formatDemoPercent } from '../../../services/dashboard/perception-demo-service';

export const DemoCard = ({ title, subtitle, right, children, className = '', bodyClassName = '' }) => (
  <Panel noHeader rounded category="secondary" height="h-auto" overflowAuto={false} className={className}>
    {(title || right) && (
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          {title && <div className="text-xs font-semibold uppercase tracking-wide text-perception-gray-300">{title}</div>}
          {subtitle && <div className="mt-1 text-xs text-perception-gray-600">{subtitle}</div>}
        </div>
        {right && <div className="shrink-0 text-xs text-perception-gray-600">{right}</div>}
      </div>
    )}
    <div className={clsx('min-w-0 flex-1', bodyClassName)}>{children}</div>
  </Panel>
);

export const DemoMetric = ({ label, value, detail, change, inverse = false, accent = 'blue', icon: Icon }) => {
  const isPositive = change !== null && change !== undefined && change !== 0;
  const favorable = isPositive && (inverse ? change < 0 : change > 0);

  return (
    <Panel noHeader rounded category="secondary" height="h-auto" overflowAuto={false}>
      <div className="flex items-start justify-between gap-3">
        <span className="text-xs text-perception-gray-400">{label}</span>
        {Icon && <Icon className="size-5 shrink-0 text-perception-gray-600"/>}
      </div>
      <div className="mt-3 text-3xl font-normal tracking-tight text-perception-gray-100">{value}</div>
      <div className="mt-2 flex min-h-5 flex-wrap items-center gap-2 text-xs">
        {isPositive && (
          <span className={clsx('flex items-center gap-0.5 font-semibold', favorable ? 'text-emerald-400' : 'text-rose-400')}>
            {change > 0 ? <ArrowUpRightIcon className="size-3.5"/> : <ArrowDownRightIcon className="size-3.5"/>}
            {change > 0 ? '+' : ''}{formatDemoNumber(change, 1)}%
          </span>
        )}
        <span className="text-perception-gray-600">{detail}</span>
      </div>
      <div className={clsx('mt-4 h-0.5 w-11 rounded-full', accent === 'teal' ? 'bg-[#4CBEBB]' : accent === 'orange' ? 'bg-[#F28725]' : accent === 'purple' ? 'bg-[#9B6CFF]' : 'bg-perception-blue')}/>
    </Panel>
  );
};

export const DemoSectionHeading = ({ title, subtitle, right }) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-perception-gray-300">{title}</div>
      {subtitle && <div className="mt-1 text-xs text-perception-gray-600">{subtitle}</div>}
    </div>
    {right && <div className="text-xs text-perception-gray-600">{right}</div>}
  </div>
);

export const DemoGauge = ({ value, label, color = '#4164FD', size = 108 }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="flex items-center justify-center rounded-full p-3" style={{ width: size, height: size, background: `conic-gradient(${color} 0% ${Math.min(100, Math.max(0, value))}%, #434343 ${Math.min(100, Math.max(0, value))}% 100%)` }}>
      <div className="flex h-full w-full items-center justify-center rounded-full bg-perception-black-500 text-xl text-perception-gray-100">
        {formatDemoPercent(value, 1)}
      </div>
    </div>
    {label && <span className="text-center text-xs text-perception-gray-400">{label}</span>}
  </div>
);

export const DemoProgress = ({ label, value, percentage, color = '#4164FD', detail }) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="truncate text-perception-gray-300">{label}</span>
      <div className="flex shrink-0 gap-2">
        <span className="text-perception-gray-300">{value}</span>
        {detail && <span className="text-perception-gray-600">{detail}</span>}
      </div>
    </div>
    <div className="h-1.5 overflow-hidden rounded-full bg-perception-black-800">
      <div className="h-full rounded-full transition-all" style={{ backgroundColor: color, width: `${Math.min(100, Math.max(0, percentage))}%` }}/>
    </div>
  </div>
);

export const DemoNote = ({ title, children, warning = false }) => (
  <div className="flex items-start gap-3 rounded-md border border-perception-black-700 bg-perception-black-800 p-4">
    {warning ? <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-[#F28725]"/> : <InformationCircleIcon className="mt-0.5 size-5 shrink-0 text-[#4CBEBB]"/>}
    <div className="text-xs leading-5 text-perception-gray-400">
      {title && <div className="mb-1 font-semibold text-perception-gray-300">{title}</div>}
      {children}
    </div>
  </div>
);

export const DemoStatus = ({ status }) => {
  const colors = {
    Corrected: 'bg-emerald-500/10 text-emerald-400',
    Rejected: 'bg-orange-500/10 text-[#FFAB63]',
    Dismissed: 'bg-perception-black-700 text-perception-gray-400',
    Online: 'bg-emerald-500/10 text-emerald-400',
  };

  return (
    <span className={clsx('inline-flex items-center gap-1 rounded px-2 py-1 text-xs', colors[status] ?? colors.Dismissed)}>
      {status === 'Online' && <CheckCircleIcon className="size-3.5"/>}
      {status}
    </span>
  );
};

export const DemoDataTable = ({ columns, rows, emptyText = 'No records in this period.', rowKey = 'id' }) => (
  <div className="overflow-x-auto rounded-md border border-perception-black-700">
    <table className="w-full min-w-[650px] text-left">
      <thead className="bg-perception-black-700">
        <tr>
          {columns.map(({ key, name, align }) => <th key={key} className={clsx('whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase text-perception-gray-400', align === 'right' && 'text-right')}>{name}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row[rowKey] ?? index} className="border-t border-perception-black-700 text-xs text-perception-gray-300 transition-colors hover:bg-perception-black-700/60">
            {columns.map(({ key, render, align }) => <td key={key} className={clsx('whitespace-nowrap px-4 py-3', align === 'right' && 'text-right')}>{render ? render(row) : row[key]}</td>)}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-perception-gray-600">{emptyText}</td></tr>}
      </tbody>
    </table>
  </div>
);
